import { createSlice } from '@reduxjs/toolkit';
import {
  fetchWatchtowerCampaigns,
  fetchWatchtowerAdsets,
  fetchWatchtowerAds,
} from '../services/watchtower';

export const WATCHTOWER_CACHE_TTL_MS = 2 * 60 * 1000;

const normalizeKind = (kind) => {
  if (!kind) return null;
  const lowered = kind.toLowerCase();
  if (lowered === 'ad_set') return 'adset';
  if (lowered === 'campaign' || lowered === 'adset' || lowered === 'ad') {
    return lowered;
  }
  return lowered;
};

const makeCacheKey = (kind, objectId) => `${kind}:${objectId}`;

const ensureBucket = (container, key) => {
  if (!key) {
    return null;
  }
  if (!container[key]) {
    container[key] = {
      ids: [],
      loading: false,
      error: null,
      errors: [],
      lastFetched: null,
      lastAttempted: null,
      nextCursor: null,
      pageSize: null,
      datePreset: null,
      effectiveStatus: null,
    };
  }
  return container[key];
};

const extractEntryMeta = (entry) => {
  if (!entry) {
    return { kind: null, objectId: null, snapshot: entry?.snapshot || {} };
  }
  const snapshot = entry.snapshot || {};
  const normalizedKind = normalizeKind(entry.kind);

  const computedCampaignId =
    snapshot.campaign?.id || snapshot.adset?.campaign_id || snapshot.ad?.campaign_id;
  const computedAdsetId = snapshot.adset?.id || snapshot.ad?.adset_id;
  const computedAdId = snapshot.ad?.id;

  const derivedKind =
    normalizedKind ||
    (snapshot.campaign ? 'campaign' : null) ||
    (snapshot.adset ? 'adset' : null) ||
    (snapshot.ad ? 'ad' : null);

  const derivedObjectId =
    entry.object_id ||
    entry.objectId ||
    (derivedKind === 'campaign' ? snapshot.campaign?.id : null) ||
    (derivedKind === 'adset' ? computedAdsetId : null) ||
    (derivedKind === 'ad' ? computedAdId : null) ||
    computedCampaignId ||
    computedAdsetId ||
    computedAdId ||
    snapshot.campaign?.id;

  return {
    kind: derivedKind,
    objectId: derivedObjectId,
    snapshot,
    accountId: entry.account_id || snapshot.campaign?.account_id || computedCampaignId || null,
  };
};

const upsertSnapshots = (state, results) => {
  if (!Array.isArray(results)) {
    return;
  }
  const fetchedAt = Date.now();
  results.forEach((entry) => {
    const { kind, objectId, snapshot, accountId } = extractEntryMeta(entry);
    if (!kind || !objectId) {
      return;
    }
    const cacheKey = makeCacheKey(kind, objectId);
    state.snapshots[cacheKey] = {
      kind,
      objectId,
      accountId,
      snapshot,
      fetchedAt,
    };
  });
};

const getNextCursor = (payload) => {
  const extract = (source) => {
    if (!source) {
      return null;
    }
    if (typeof source === 'string') {
      return source || null;
    }
    if (Array.isArray(source.accounts)) {
      const accountEntry = source.accounts.find((entry) => entry?.next_cursor);
      if (accountEntry?.next_cursor) {
        return accountEntry.next_cursor;
      }
    }
    return source.next_cursor || source.next || source.cursor || null;
  };

  return (
    extract(payload?.pagination) ||
    extract(payload?.meta) ||
    extract(payload?.paging) ||
    extract(payload)
  );
};

const getPageSize = (actionParams, pagination) => {
  if (actionParams && actionParams.page_size !== undefined) {
    return actionParams.page_size;
  }
  if (pagination && pagination.page_size !== undefined) {
    return pagination.page_size;
  }
  if (pagination && pagination.limit !== undefined) {
    return pagination.limit;
  }
  if (pagination && Array.isArray(pagination.accounts)) {
    const accountEntry = pagination.accounts.find(
      (entry) => entry?.page_size !== undefined
    );
    if (accountEntry && accountEntry.page_size !== undefined) {
      return accountEntry.page_size;
    }
  }
  return null;
};

const initialState = {
  snapshots: {},
  campaigns: {
    ids: [],
    loading: false,
    error: null,
    errors: [],
    lastFetched: null,
    lastAttempted: null,
    nextCursor: null,
    pageSize: null,
    datePreset: null,
    effectiveStatus: null,
  },
  adsetsByCampaign: {},
  adsByAdset: {},
};

const watchtowerSlice = createSlice({
  name: 'watchtower',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWatchtowerCampaigns.pending, (state, action) => {
        const append = Boolean(action.meta.arg?.append);
        state.campaigns.loading = true;
        state.campaigns.error = null;
        state.campaigns.lastAttempted = Date.now();
        if (!append) {
          state.campaigns.nextCursor = null;
          state.campaigns.errors = [];
        }
      })
      .addCase(fetchWatchtowerCampaigns.fulfilled, (state, action) => {
        state.campaigns.loading = false;
        state.campaigns.error = null;
        const append = Boolean(action.meta.arg?.append);
        const results = action.payload?.results || [];
        const pagination = action.payload?.pagination;
        const nextCursor = getNextCursor(pagination);
        const resolvedPageSize = getPageSize(action.meta.arg?.params, pagination);

        upsertSnapshots(state, results);

        const newIds = results
          .map((entry) => {
            const { kind, objectId } = extractEntryMeta(entry);
            return kind && objectId ? makeCacheKey(kind, objectId) : null;
          })
          .filter(Boolean);

        if (append) {
          const existing = new Set(state.campaigns.ids);
          newIds.forEach((id) => {
            if (!existing.has(id)) {
              state.campaigns.ids.push(id);
              existing.add(id);
            }
          });
        } else {
          state.campaigns.ids = newIds;
        }

        state.campaigns.errors = action.payload?.errors || [];
        state.campaigns.nextCursor = nextCursor;
        if (resolvedPageSize !== null) {
          state.campaigns.pageSize = resolvedPageSize;
        }
        state.campaigns.datePreset =
          action.meta.arg?.params?.date_preset || state.campaigns.datePreset || 'last_7d';
        state.campaigns.effectiveStatus =
          action.meta.arg?.params?.effective_status ||
          state.campaigns.effectiveStatus ||
          'ACTIVE';
        state.campaigns.lastFetched = Date.now();
      })
      .addCase(fetchWatchtowerCampaigns.rejected, (state, action) => {
        state.campaigns.loading = false;
        state.campaigns.error = action.payload || action.error.message;
      });

    builder
      .addCase(fetchWatchtowerAdsets.pending, (state, action) => {
        const campaignId = action.meta.arg?.campaignId;
        const append = Boolean(action.meta.arg?.append);
        const bucket = ensureBucket(state.adsetsByCampaign, campaignId);
        if (bucket) {
          bucket.loading = true;
          bucket.error = null;
          bucket.lastAttempted = Date.now();
          if (!append) {
            bucket.nextCursor = null;
            bucket.errors = [];
          }
        }
      })
      .addCase(fetchWatchtowerAdsets.fulfilled, (state, action) => {
        const campaignId = action.payload?.campaignId;
        const append = Boolean(action.meta.arg?.append);
        const bucket = ensureBucket(state.adsetsByCampaign, campaignId);
        const pagination = action.payload?.pagination;
        const nextCursor = getNextCursor(pagination);
        const resolvedPageSize = getPageSize(action.meta.arg?.params, pagination);

        if (bucket) {
          bucket.loading = false;
          bucket.error = null;
        }
        const results = action.payload?.results || [];
        upsertSnapshots(state, results);
        if (bucket) {
          const newIds = results
            .map((entry) => {
              const { kind, objectId } = extractEntryMeta(entry);
              return kind && objectId ? makeCacheKey(kind, objectId) : null;
            })
            .filter(Boolean);

          if (append) {
            const existing = new Set(bucket.ids);
            newIds.forEach((id) => {
              if (!existing.has(id)) {
                bucket.ids.push(id);
                existing.add(id);
              }
            });
          } else {
            bucket.ids = newIds;
          }

          bucket.errors = action.payload?.errors || [];
          bucket.nextCursor = nextCursor;
          if (resolvedPageSize !== null) {
            bucket.pageSize = resolvedPageSize;
          }
          bucket.datePreset =
            action.meta.arg?.params?.date_preset || bucket.datePreset || 'last_7d';
          bucket.effectiveStatus =
            action.meta.arg?.params?.effective_status || bucket.effectiveStatus || 'ACTIVE';
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchWatchtowerAdsets.rejected, (state, action) => {
        const campaignId = action.meta.arg?.campaignId;
        const bucket = ensureBucket(state.adsetsByCampaign, campaignId);
        if (bucket) {
          bucket.loading = false;
          bucket.error = action.payload || action.error.message;
        }
      });

    builder
      .addCase(fetchWatchtowerAds.pending, (state, action) => {
        const adsetId = action.meta.arg?.adsetId;
        const append = Boolean(action.meta.arg?.append);
        const bucket = ensureBucket(state.adsByAdset, adsetId);
        if (bucket) {
          bucket.loading = true;
          bucket.error = null;
          bucket.lastAttempted = Date.now();
          if (!append) {
            bucket.nextCursor = null;
            bucket.errors = [];
          }
        }
      })
      .addCase(fetchWatchtowerAds.fulfilled, (state, action) => {
        const adsetId = action.payload?.adsetId;
        const append = Boolean(action.meta.arg?.append);
        const bucket = ensureBucket(state.adsByAdset, adsetId);
        const pagination = action.payload?.pagination;
        const nextCursor = getNextCursor(pagination);
        const resolvedPageSize = getPageSize(action.meta.arg?.params, pagination);

        if (bucket) {
          bucket.loading = false;
          bucket.error = null;
        }
        const results = action.payload?.results || [];
        upsertSnapshots(state, results);
        if (bucket) {
          const newIds = results
            .map((entry) => {
              const { kind, objectId } = extractEntryMeta(entry);
              return kind && objectId ? makeCacheKey(kind, objectId) : null;
            })
            .filter(Boolean);

          if (append) {
            const existing = new Set(bucket.ids);
            newIds.forEach((id) => {
              if (!existing.has(id)) {
                bucket.ids.push(id);
                existing.add(id);
              }
            });
          } else {
            bucket.ids = newIds;
          }

          bucket.errors = action.payload?.errors || [];
          bucket.nextCursor = nextCursor;
          if (resolvedPageSize !== null) {
            bucket.pageSize = resolvedPageSize;
          }
          bucket.datePreset =
            action.meta.arg?.params?.date_preset || bucket.datePreset || 'last_7d';
          bucket.effectiveStatus =
            action.meta.arg?.params?.effective_status || bucket.effectiveStatus || 'ACTIVE';
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchWatchtowerAds.rejected, (state, action) => {
        const adsetId = action.meta.arg?.adsetId;
        const bucket = ensureBucket(state.adsByAdset, adsetId);
        if (bucket) {
          bucket.loading = false;
          bucket.error = action.payload || action.error.message;
        }
      });
  },
});

export const selectWatchtowerState = (state) => state.watchtower;
export const watchtowerReducer = watchtowerSlice.reducer;
export const makeWatchtowerCacheKey = makeCacheKey;
export const parseWatchtowerCacheKey = (cacheKey) => {
  if (!cacheKey) {
    return { kind: null, objectId: null };
  }
  const [kind, ...rest] = cacheKey.split(':');
  return {
    kind: kind || null,
    objectId: rest.length ? rest.join(':') : null,
  };
};
